import { useEffect, useRef } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useConfig } from '../lib/ConfigContext';

interface Props {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  required?: boolean;
}

// Wraps the Google Maps Places library + native PlaceAutocompleteElement (or legacy
// Autocomplete) so users can type a partial address and pick a real, normalized one.
// Falls back to a plain text input when no API key is configured.
export function AddressAutocomplete(props: Props) {
  const config = useConfig();
  if (!config.googleMapsApiKey) {
    return (
      <input
        type="text"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className={props.className}
        placeholder={props.placeholder}
        required={props.required}
      />
    );
  }
  return (
    <APIProvider apiKey={config.googleMapsApiKey} libraries={['places']}>
      <PlacesInput {...props} />
    </APIProvider>
  );
}

function PlacesInput({ value, onChange, className, placeholder, required }: Props) {
  const places = useMapsLibrary('places');
  const inputRef = useRef<HTMLInputElement>(null);
  // The parent (CreateHomePage) passes an inline arrow on every render — so the
  // identity of `onChange` changes per keystroke. Capturing it in a ref means
  // the effect below only depends on `places`, and the Google Autocomplete
  // widget is built once per Maps library load instead of being torn down and
  // re-attached on every keystroke (which leaked the prior widget's internal
  // listeners on the same <input>).
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!places || !inputRef.current) return;
    // Legacy `places.Autocomplete` is widely available; Google's newer PlaceAutocompleteElement
    // is a Web Component that's harder to style consistently with our form. The legacy widget
    // is still fully supported through 2026 (Google's deprecation notice).
    const ac = new places.Autocomplete(inputRef.current, {
      fields: ['formatted_address', 'address_components', 'geometry'],
      types: ['address'],
      // Bias to North America since all Manor homes are there today (just makes results faster
      // — users can still pick non-NA addresses if needed).
      componentRestrictions: { country: ['ca', 'us'] },
    });
    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      const addr = place.formatted_address;
      if (addr) onChangeRef.current(addr);
    });
    return () => {
      listener.remove();
      // Google injects pac-container divs as siblings of <body>; let them GC naturally.
    };
  }, [places]);

  // Suppress browser autofill which fights with Places dropdown. Controlled
  // `value` (not `defaultValue`) so a parent reset/prefill is reflected — a
  // defaultValue input never picks up post-mount changes from props.
  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={className}
      placeholder={placeholder}
      required={required}
      autoComplete="off"
    />
  );
}
